/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { Fragment, useState } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { Divider, Grid, Link, Paper, Typography } from '@material-ui/core';
import colors from '../../../../../themes/main-colors';
import theme from '../../../../../themes/theme';
import PopoverToolTip from '../../../../pge-plus-common/PopoverToolTip';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import { CheckBoxes } from '../../../../pge-plus-common/CheckBoxes';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import routes from '../../../../../routes';
import { PgeEvModel } from '../../../../pge-plus-common/PgeEvModel';
import Buttons from '../../../../buttons';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import useERApplicationApi from '../../../../../hooks/pge-plus/useERApplicationApi';
import useUtil from '../../../../../hooks/pge-plus/useUtil';
import { rebateConfirmERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import {
  ApplicationErSectionType,
  ApplicationSectionStatus,
  SectionErRebateConfirm,
} from '../../../../../__generated__/pgeplus-types';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import { getFormattedCurrencyString } from '../../../../../util/format';

// CUP-24447: Renders a blank dash instead of "$NaN" for a rebate amount not yet calculated (wait for BE gap fix.. TODO: REVISIT... update PENDING_CALCULATION )
const formatCurrencyOrBlank = (value: number | undefined): string => {
  if (value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return Number.isInteger(Number(value))
    ? `$${getFormattedCurrencyString(Number(value))}`
    : `$${getFormattedCurrencyString(Number(value).toFixed(2))}`;
};

type RebateData = {
  standardChargerRebate?: number;
  standardPanelUpgradRebate?: number;
  chargerModel?: string;
  chargerCost?: number;
  installationCost?: number;
  totalCost?: number;
  totalRebates?: number;
};

interface RebateResultsProps {
  title?: string;
  eligibilityText?: string;
  isStandardOrChargerRebate?: boolean;
  rebateData?: RebateData;
  rebateLabels?: {
    charger?: string;
    panelUpgrade?: string;
  };
  backRoute?: string;
  onEnroll?: () => void;
  hasCharger?: boolean;
}

const RebateResults: React.FC<RouteComponentProps & RebateResultsProps> = (
  props: RouteComponentProps & RebateResultsProps,
) => {
  const {
    title,
    eligibilityText,
    isStandardOrChargerRebate,
    rebateLabels,
    rebateData,
    backRoute = routes.PGE_PLUS_REBATE_ONLY_CONNECT_TESLA,
    hasCharger = true,
  } = props;
  const [acceptChargerConnected, setacceptChargerConnected] = useState<
    string[]
  >([]);
  const [acceptEv, setAcceptEv] = useState<string[]>([]);
  const [acceptTnC, setAcceptTnC] = useState<string[]>([]);
  const [isPGEEvModelVisible, setIsPGEEvModelVisible] = useState<boolean>(
    false,
  );
  const [formValidated, setFormValidated] = useState<boolean>(false);
  const { wrapWithLoader } = useWrapWithLoader();
  const { updateSectionRebateConfirm } = useUpdateERSections();
  const { submitApplication } = useERApplicationApi();
  const { setErrorNotification } = useUtil();

  const {
    formState: { rebateConfirm, application },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const isAccepted =
    acceptTnC.length > 0 &&
    acceptEv.length > 0 &&
    (!hasCharger || acceptChargerConnected.length > 0);

  const sectionDataRebateConfirm: SectionErRebateConfirm = getSectionData(
    ApplicationErSectionType.ErRebateConfirm,
  );

  const onEnroll = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateConfirm(
        rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            // CUP-24447: Only confirm charger-connected-to-meter for customers who actually have a charger
            confirmChargerConnectedToResidentialMeter: hasCharger
              ? true
              : undefined,
            confirmDriveElectricVehicle: true,
            agreeToTermsAndConditions: true,
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
          ApplicationSectionStatus.Completed,
        ),
      );
      if (!response.data?.updateSectionERRebateConfirm.section.errors.length) {
        if (response.data?.updateSectionERRebateConfirm?.section.data) {
          const updatedValues = rebateConfirmERFormStateAdapter().toFormState(
            response.data?.updateSectionERRebateConfirm?.section.data,
          );
          updateFormState('rebateConfirm', {
            ...updatedValues,
          });
        }
        const { data, errors } = await submitApplication({
          applicationId: application?.id!,
        });
        if (errors) {
          throw new Error();
        } else {
          //Route according to Ineligibility case
          const IneligibilityCode =
            data?.submitApplicationER?.ineligibilityReasons[0]?.code;

          if (IneligibilityCode) {
            void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
              //state: { IneligibilityCode },
            });
          } else {
            void navigate(routes.PGE_PLUS_REBATE_ONLY_SUCCESS);
          }
          return;
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const { content, richText, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_REBATE_RESULTS,
  );

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateConfirm({
        ...rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            confirmChargerConnectedToResidentialMeter: hasCharger
              ? true
              : undefined,
            confirmDriveElectricVehicle: true,
            agreeToTermsAndConditions: true,
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateConfirm?.validation?.errors ||
        response.data?.updateSectionERRebateConfirm?.section?.errors.length !==
          0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <form
      onSubmit={async e => {
        e.preventDefault();
        setFormValidated(true);
        await onEnroll();
      }}
      noValidate
    >
      <Grid container>
        <Grid item>
          <Typography
            variant={'body1'}
            css={{
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 24,
              [theme.breakpoints.down('xs')]: {
                fontSize: 16,
              },
            }}
          >
            {title}
          </Typography>
        </Grid>
        <Grid
          item
          container
          css={{
            backgroundColor: colors.white,
            borderRadius: '5px',
            boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
            marginBottom: 20,
          }}
        >
          <Grid
            item
            css={{
              backgroundColor: colors.lightBlue1,
              padding: 15,
            }}
          >
            <Typography
              variant={'body1'}
              css={{
                fontSize: 20,
                [theme.breakpoints.down('xs')]: {
                  fontSize: 16,
                },
              }}
            >
              {eligibilityText}
            </Typography>
          </Grid>
          <Grid item container css={{ rowGap: 15, padding: 15 }}>
            {isStandardOrChargerRebate && (
              <Fragment>
                <Grid item>
                  <Typography
                    variant={'body1'}
                    css={{
                      fontSize: 20,
                      fontWeight: 700,
                      [theme.breakpoints.down('xs')]: {
                        fontSize: 16,
                      },
                    }}
                  >
                    {content.get('REUSALBE_CHARGER')}
                  </Typography>
                </Grid>
                <Grid item container justify={'space-between'}>
                  <Grid item>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {rebateData?.chargerModel}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {formatCurrencyOrBlank(rebateData?.chargerCost)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider css={{ color: colors.gray93 }} />
                </Grid>
                <Grid item>
                  <Typography
                    variant={'body1'}
                    css={{
                      fontSize: 20,
                      fontWeight: 700,
                      [theme.breakpoints.down('xs')]: {
                        fontSize: 16,
                      },
                    }}
                  >
                    {content.get('REUSABLE_INSTALLATION')}
                  </Typography>
                </Grid>
                <Grid item container justify={'space-between'}>
                  <Grid item>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {content.get('REUSALBE_INSTALLATION_COSTS')}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {formatCurrencyOrBlank(rebateData?.installationCost)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider css={{ color: colors.gray93 }} />
                </Grid>
                <Grid item container justify={'space-between'}>
                  <Grid item>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        fontWeight: 700,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {content.get('REUSABLE_TOTAL_COST')}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        fontWeight: 700,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {formatCurrencyOrBlank(rebateData?.totalCost)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider css={{ color: colors.gray93 }} />
                </Grid>
                <Grid item container justify={'space-between'}>
                  <Grid item xs={8}>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {rebateLabels?.charger}
                    </Typography>
                  </Grid>
                  <Grid item xs={3} css={{ textAlign: 'end' }}>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {formatCurrencyOrBlank(rebateData?.standardChargerRebate)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item container justify={'space-between'}>
                  <Grid item xs={8}>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {rebateLabels?.panelUpgrade}
                    </Typography>
                  </Grid>
                  <Grid item xs={3} css={{ textAlign: 'end' }}>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        [theme.breakpoints.down('xs')]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {formatCurrencyOrBlank(
                        rebateData?.standardPanelUpgradRebate,
                      )}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider css={{ color: colors.gray93 }} />
                </Grid>
              </Fragment>
            )}
            <Grid item container justify={'space-between'}>
              <Grid item xs={8}>
                <Typography
                  variant={'body1'}
                  css={{
                    fontSize: 18,
                    fontWeight: 700,
                    [theme.breakpoints.down('xs')]: {
                      fontSize: 16,
                    },
                    '& a': { textDecoration: 'none' },
                  }}
                >
                  {inlineRichText(
                    content.get('TOTAL_REBATES_WHEN_YOU_ENROLL_SMARTCHARGING'),
                  )}
                </Typography>
              </Grid>
              <Grid item xs={4} css={{ textAlign: 'end' }}>
                <Typography
                  variant={'body1'}
                  css={{
                    fontSize: 20,
                    fontWeight: 700,
                    [theme.breakpoints.down('xs')]: {
                      fontSize: 16,
                    },
                  }}
                >
                  {formatCurrencyOrBlank(rebateData?.totalRebates)}
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Divider css={{ color: colors.gray93 }} />
            </Grid>
            <Grid item>
              <Typography
                variant={'body1'}
                css={{
                  fontSize: 20,
                  fontWeight: 700,
                  [theme.breakpoints.down('xs')]: {
                    fontSize: 16,
                  },
                }}
              >
                {content.get('TAX_CREDIT')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <PopoverToolTip
                popoverContent={content.get('TAX_CREDIT_POPOVER_CONTENT')}
              />
            </Grid>
          </Grid>
        </Grid>
        {((hasCharger && acceptChargerConnected.length < 1) ||
          acceptEv.length < 1) &&
          formValidated && (
            <Typography
              variant="body2"
              css={{ fontSize: 16, color: colors.roseRed, marginTop: 20 }}
            >
              {content.get('CHECKBOXES_REQUIRED_TEXT')}
            </Typography>
          )}
        <Grid item>
          <Typography
            variant="body2"
            css={{
              fontSize: 16,
              color:
                formValidated && acceptTnC.length < 1
                  ? colors.roseRed
                  : colors.noirBlur,
              '& span': {
                color: 'red',
              },
            }}
          >
            {richText(content.get('CUSTOMER_QUOTE_INSTRUCTIONS'))}
          </Typography>
        </Grid>
        {hasCharger && (
          <Grid item>
            <Typography
              variant="body2"
              css={{
                marginTop: '20px',
                '& .MuiTypography-body1': {
                  fontSize: '18px',
                  fontWeight: 'normal',
                },
                '& .MuiButtonBase-root': {
                  padding: '0 9px',
                },
                '& .MuiFormControlLabel-root': {
                  marginRight: 0,
                },
              }}
            >
              <CheckBoxes
                options={[
                  {
                    label: content.get('CONFIRM_CHARGER_IS_HARDWIRED')!,
                    value: content.get('CONFIRM_CHARGER_IS_HARDWIRED')!,
                    required: '*',
                  },
                ]}
                state={acceptChargerConnected}
                setState={setacceptChargerConnected}
              />
            </Typography>
          </Grid>
        )}
        <Grid item css={{ marginTop: 5, marginBottom: 25 }}>
          <Typography
            variant="body2"
            css={{
              marginTop: '20px',
              '& .MuiTypography-body1': {
                fontSize: '18px',
                fontWeight: 'normal',
              },
              '& .MuiButtonBase-root': {
                padding: '0 9px',
              },
              '& .MuiFormControlLabel-root': {
                marginRight: 0,
              },
            }}
          >
            <CheckBoxes
              options={[
                {
                  label: content.get('CONFIRM_I_DRIVE_EV')!,
                  value: content.get('CONFIRM_I_DRIVE_EV')!,
                  required: '*',
                },
              ]}
              state={acceptEv}
              setState={setAcceptEv}
            />
          </Typography>
        </Grid>
        <Grid item css={{ marginBottom: 15 }}>
          <Typography
            variant="body2"
            css={{
              fontSize: 16,
              color:
                formValidated && acceptTnC.length < 1
                  ? colors.roseRed
                  : colors.noirBlur,
            }}
          >
            {content.get('CUSTOMER_QUOTE_PLEASE_REVIEW_TCS')}
          </Typography>
        </Grid>
        <Grid
          item
          container
          css={{
            backgroundColor: colors.white,
            borderRadius: '5px',
            boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
            border:
              formValidated && acceptTnC.length < 1
                ? `1px ${colors.roseRed} solid`
                : 'none',
            padding: 12,
            marginBottom: 18,
          }}
        >
          <Paper
            css={{
              width: '100%',
              backgroundColor: colors.lightGray2,
              maxHeight: '386px',
              padding: '8px',
              textAlign: 'left',
              overflow: 'auto',
              boxShadow: 'none',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: colors.lightGray1,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: colors.sparkBlue,
                borderRadius: '8px',
                maxHeight: '20px',
              },
            }}
          >
            <Typography
              component="p"
              variant="h3"
              css={{
                fontWeight: 700,
                fontFamily: 'Forma-DJR-Display',
                marginBottom: '15px',
              }}
            >
              {content.get('REUSABLE_PGE_PLUS_T_N_C')}
            </Typography>
            <Typography
              variant="h6"
              css={{
                fontWeight: 400,
                marginTop: '20px',
                fontFamily: 'Untitled-Sans',
                lineHeight: 'normal',
                marginBottom: '10px',
              }}
            >
              <Link
                href={content.get('REUSABLE_PGEPLUS_TERMS_AND_CONDITIONS_LINK')}
                target="_blank"
                css={{
                  fontWeight: 700,
                  fontSize: '16px',
                  fontFamily: 'Untitled-Sans',
                  color: colors.sparkBlue,
                  lineHeight: '20px',
                  textDecoration: 'underline',
                }}
              >
                {content.get('REUSABLE_TERMS_AND_CONDITIONS')}
              </Link>
            </Typography>
          </Paper>
          <Grid item xs={12}>
            <Typography
              variant="body2"
              css={{
                marginTop: '20px',
                '& .MuiTypography-body1': {
                  fontSize: '18px',
                  fontWeight: 'normal',
                },
                '& .MuiButtonBase-root': {
                  padding: '0 9px',
                },
                '& .MuiFormControlLabel-root': {
                  marginRight: 0,
                },
              }}
            >
              <CheckBoxes
                options={[
                  {
                    label: content.get('TERMS_AND_CONDITIONS')!,
                    value: content.get('TERMS_AND_CONDITIONS')!,
                    required: '*',
                  },
                ]}
                state={acceptTnC}
                setState={setAcceptTnC}
              />
            </Typography>
          </Grid>
        </Grid>
        <Divider css={{ color: colors.gray93, margin: '20px 0 20px 0' }} />
        <Grid
          item
          container
          spacing={2}
          css={{
            marginTop: 24,
            marginBottom: 24,
            justifyContent: 'center',
          }}
        >
          <Grid item xs={6} md={3}>
            <Link onClick={() => void navigate(backRoute)}>
              <Buttons
                type="button"
                fullWidth
                variant="outlined"
                color="secondary"
                css={{
                  fontSize: '18px',
                  borderRadius: '30px',
                  '.MuiButton-label': {
                    fontWeight: 500,
                  },
                  '&:hover': {
                    borderWidth: '2px',
                  },
                }}
              >
                {content.get('REUSABLE_BUTTON_BACK')}
              </Buttons>
            </Link>
          </Grid>
          <Grid item xs={6} md={3}>
            <Buttons
              type="submit"
              fullWidth
              variant="contained"
              css={{
                fontSize: '18px',
                borderRadius: '30px',
                '.MuiButton-label': {
                  fontWeight: 500,
                },
                backgroundColor: colors.orange,
              }}
              disabled={isAccepted ? false : true}
            >
              {content.get('REUSABLE_ENROLL_BUTTON')}
            </Buttons>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <SaveAndComeBackLink
            content={content}
            onSaveAndExit={handleSaveAndExit}
          />
        </Grid>
      </Grid>
      <PgeEvModel
        isVisible={isPGEEvModelVisible}
        setIsVisible={setIsPGEEvModelVisible}
      />
    </form>
  );
};

export default RebateResults;
